import React, { useState, useEffect } from "react";
import {
  Menu,
  MenuItem,
  ListItemText,
  Button,
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Skeleton,
} from "@mui/material";
import { motion } from "framer-motion";
import { MenuButtonsProps } from "../../../types";
import {
  menuPaperStyles,
  // defaultMenuItem,
  typographyStyle,
  selectedItemContainer,
  selectedItemBox,
  selectedItemTitle,
  selectedItemDescription,
  goButton,
} from "./styles";
import { useQuizeStore } from "../../../stores/quizeStore";
import { AuthenticatedMenuImage } from "../../authenticated-media/authenticated-menu-image";
// import images from "../../../assets/images";

interface MenuDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  menuButtons: MenuButtonsProps[];
  onSendMessage: (command: string, hideFromUser?: boolean) => void;
  onClose: () => void;
  isStatic?: boolean;
}

export const MenuDropdown: React.FC<MenuDropdownProps> = ({
  anchorEl,
  open,
  menuButtons,
  onSendMessage,
  onClose,
  isStatic = false,
}) => {
  const { fetchPreassessmentForce, toggleNeedCloseButton } = useQuizeStore();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isMobileLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 900px) and (pointer: coarse)",
  );
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  const fullMenuButtons = (() => {
    const list = [...menuButtons];
    const roleplayIndex = list.findIndex((b) => b.text === "Roleplays");
    if (roleplayIndex > 0) {
      const [roleplay] = list.splice(roleplayIndex, 1);
      return [roleplay, ...list];
    }
    return list;
  })();
  const menuButtonsKey = fullMenuButtons
    .map((b) => b.image || b.text || "")
    .join("|");

  // Reset selected when menu opens; reset loaded images only when menu content changes
  useEffect(() => {
    if (open) {
      setSelectedIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    setLoadedImages(new Set());
  }, [menuButtonsKey]);

  const handleImageLoad = (src: string) => {
    setLoadedImages((prev) => new Set(prev).add(src));
  };

  const handleMenuCommand = (command: string, isPreAssessment = false) => {
    if (isPreAssessment) {
      toggleNeedCloseButton();
      fetchPreassessmentForce();
    } else {
      onSendMessage(command, true);
    }
    onClose();
  };

  const handleClose = () => {
    setSelectedIndex(-1);
    onClose();
  };

  if (isStatic) {
    return (
      <Box
        sx={{
          p: isMobile ? 1.25 : 2,
          background: theme.palette.background.paper,
          borderRadius: "18px",
          marginBottom: isMobile ? "10px" : "25px",
          width: isMobileLandscape ? "200px" : isMobile ? "220px" : "273px",
          maxHeight: isMobileLandscape ? "none" : isMobile ? "43dvh" : "none",
          overflowY: isMobileLandscape
            ? "visible"
            : isMobile
              ? "auto"
              : "visible",
          "&::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          scrollbarWidth: "none",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "6px" : "13px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            marginBottom: isMobile ? "4px" : "10px",
            fontSize: isMobileLandscape ? "14px" : isMobile ? "16px" : "24px",
            color: theme.palette.text.primary,
          }}
        >
          What would you like to do?
        </Typography>
        {fullMenuButtons.length > 0 ? (
          fullMenuButtons.map((button, index) => {
            if (selectedIndex === index) {
              return (
                <Box key={index} sx={selectedItemContainer}>
                  <Box sx={{ ...selectedItemBox, marginLeft: "8px" }}>
                    <Typography sx={selectedItemTitle}>
                      {button.text}
                    </Typography>
                    {button.description && (
                      <Typography sx={selectedItemDescription}>
                        {button.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            } else {
              return (
                <Box
                  key={index}
                  component={motion.div}
                  whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: isMobileLandscape
                      ? "3px 6px"
                      : isMobile
                        ? "4px 8px"
                        : "5px 20px",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedIndex(index)}
                >
                  {button.Icon ? (
                    <button.Icon
                      style={{
                        width: isMobileLandscape ? 16 : 20,
                        height: isMobileLandscape ? 16 : 20,
                        marginRight: 8,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <>
                      {!loadedImages.has(button.image) && (
                        <Skeleton
                          variant="rectangular"
                          width={isMobileLandscape ? 16 : 20}
                          height={isMobileLandscape ? 16 : 20}
                          sx={{
                            marginRight: "8px",
                            borderRadius: "6px",
                          }}
                        />
                      )}
                      <AuthenticatedMenuImage
                        src={button.image}
                        sx={{
                          width: isMobileLandscape ? 16 : 20,
                          height: isMobileLandscape ? 16 : 20,
                          marginRight: "8px",
                          borderRadius: "6px",
                        }}
                        visible={loadedImages.has(button.image)}
                        onLoad={() => handleImageLoad(button.image)}
                      />
                    </>
                  )}
                  <Typography
                    sx={{
                      color: theme.palette.text.primary,
                      fontSize: isMobileLandscape
                        ? "11px"
                        : isMobile
                          ? "12px"
                          : "16px",
                      fontWeight: 500,
                    }}
                  >
                    {button.text}
                  </Typography>
                </Box>
              );
            }
          })
        ) : (
          <MenuItem disabled>
            <ListItemText
              primary="Loading menu..."
              primaryTypographyProps={{ sx: typographyStyle }}
            />
          </MenuItem>
        )}
      </Box>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      disableAutoFocus
      disableEnforceFocus
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={menuPaperStyles}
    >
      <MenuItem disabled>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: theme.palette.text.primary,
            textAlign: "center",
            width: "100%",
            fontSize: isMobileLandscape ? "1rem" : undefined,
          }}
        >
          What would you like to do?
        </Typography>
      </MenuItem>
      {fullMenuButtons.map((button, index) => {
        if (selectedIndex === index) {
          return (
            <MenuItem key={index} sx={selectedItemContainer}>
              <Button
                variant="contained"
                onClick={() =>
                  handleMenuCommand(
                    button.command,
                    button.text === "Pre-assessment",
                  )
                }
                sx={goButton}
              >
                Go
              </Button>
              <Box
                key={index}
                component={motion.div}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  marginLeft: "8px",
                }}
                onClick={() => setSelectedIndex(index)}
              >
                <Typography sx={selectedItemTitle}>{button.text}</Typography>
                {button.description && (
                  <Typography sx={selectedItemDescription}>
                    {button.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        } else {
          return (
            <Box
              key={index}
              component={motion.div}
              whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: isMobileLandscape ? "3px 10px" : "5px 20px",
                cursor: "pointer",
              }}
              onClick={() => setSelectedIndex(index)}
            >
              {button.Icon ? (
                <button.Icon
                  style={{
                    width: isMobileLandscape ? 16 : 20,
                    height: isMobileLandscape ? 16 : 20,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <>
                  {!loadedImages.has(button.image) && (
                    <Skeleton
                      variant="rectangular"
                      width={isMobileLandscape ? 16 : 20}
                      height={isMobileLandscape ? 16 : 20}
                      sx={{
                        marginRight: "8px",
                        borderRadius: "6px",
                      }}
                    />
                  )}
                  <AuthenticatedMenuImage
                    src={button.image}
                    sx={{
                      width: isMobileLandscape ? 16 : 20,
                      height: isMobileLandscape ? 16 : 20,
                      marginRight: "8px",
                      borderRadius: "6px",
                    }}
                    visible={loadedImages.has(button.image)}
                    onLoad={() => handleImageLoad(button.image)}
                  />
                </>
              )}
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: isMobileLandscape ? "14px" : "16px",
                  fontWeight: 500,
                }}
              >
                {button.text}
              </Typography>
            </Box>
          );
        }
      })}
    </Menu>
  );
};
